import React, { useState } from 'react';
import { useTaskStore, Task } from '../../stores/taskStore';
import { chemistryApi } from '../../api/chemistry';

const TaskProgressBar: React.FC = () => {
  const { tasks, removeTask, updateTask } = useTaskStore();

  if (tasks.length === 0) return null;

  const handleCancel = async (task: Task) => {
    try {
      if (task.type === 'merge') {
        await chemistryApi.cancelMerge(task.id);
      } else if (task.type === 'download') {
        await chemistryApi.cancelDownload(task.id);
      } else if (task.type === 'upload' && task.fileId) {
        await chemistryApi.cancelUpload(String(task.fileId));
      }
      updateTask(task.id, { status: 'cancelled' });
    } catch (err) {
      console.error('Failed to cancel task:', err);
    }
  };

  const handleDownloadComplete = (task: Task) => {
    if (task.downloadUrl) {
      // Trigger browser download
      const link = document.createElement('a');
      link.href = task.downloadUrl;
      link.download = task.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="bg-gray-50 border-b border-gray-200">
      {tasks.map((task) => (
        <TaskRow
          key={task.id}
          task={task}
          onDismiss={() => removeTask(task.id)}
          onCancel={() => handleCancel(task)}
          onDownloadComplete={() => handleDownloadComplete(task)}
          onAutoDismiss={() => removeTask(task.id)}
        />
      ))}
    </div>
  );
};

interface TaskRowProps {
  task: Task;
  onDismiss: () => void;
  onCancel: () => void;
  onDownloadComplete?: () => void;
  onAutoDismiss?: () => void;
}

const TaskRow: React.FC<TaskRowProps> = ({ task, onDismiss, onCancel, onDownloadComplete, onAutoDismiss }) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const [downloadTriggered, setDownloadTriggered] = useState(false);

  const isRunning = task.status === 'running';
  const isError = task.status === 'error';
  const isCompleted = task.status === 'completed';
  const isCancelled = task.status === 'cancelled';

  // Auto-trigger download when download task completes
  React.useEffect(() => {
    if (task.type === 'download' && isCompleted && task.downloadUrl && !downloadTriggered) {
      setDownloadTriggered(true);
      onDownloadComplete?.();
    }
  }, [task.type, isCompleted, task.downloadUrl, downloadTriggered, onDownloadComplete]);

  // Auto-dismiss completed/cancelled tasks after 5 seconds
  React.useEffect(() => {
    if (isCompleted || isCancelled) {
      const timer = setTimeout(() => {
        onAutoDismiss?.();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isCompleted, isCancelled, onAutoDismiss]);

  const bgColor = isError
    ? 'bg-red-50'
    : isCancelled
    ? 'bg-yellow-50'
    : isCompleted
    ? 'bg-green-50'
    : 'bg-blue-50';

  const barColor = isError
    ? 'bg-red-500'
    : isCancelled
    ? 'bg-yellow-500'
    : isCompleted
    ? 'bg-green-500'
    : 'bg-emerald-500';

  const textColor = isError
    ? 'text-red-700'
    : isCancelled
    ? 'text-yellow-700'
    : isCompleted
    ? 'text-green-700'
    : 'text-gray-700';

  const typeLabel = task.type === 'merge' ? 'Merging'
    : task.type === 'upload' ? 'Uploading'
    : task.type === 'download' ? 'Downloading'
    : 'Processing';

  // Format molecule count with commas
  const moleculeCountStr = task.totalMolecules
    ? ` ${task.totalMolecules.toLocaleString()} molecules`
    : '';

  const handleCancel = async () => {
    setIsCancelling(true);
    await onCancel();
    setIsCancelling(false);
  };

  return (
    <div className={`px-6 py-3 ${bgColor} flex items-center gap-4`}>
      {/* Spinner or status icon */}
      <div className="flex-shrink-0">
        {isRunning ? (
          <svg className="w-5 h-5 text-emerald-500 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : isCompleted ? (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : isCancelled ? (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
      </div>

      {/* Task info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${textColor}`}>
            {typeLabel}{moleculeCountStr}: {task.fileName}
          </span>
          {isError && task.error && (
            <span className="text-sm text-red-600">- {task.error}</span>
          )}
          {isCancelled && (
            <span className="text-sm text-yellow-600">- Cancelled</span>
          )}
        </div>

        {/* Progress bar for running tasks */}
        {isRunning && (
          <div className="mt-1.5 flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden max-w-md">
              <div
                className={`h-full ${barColor} transition-all duration-300`}
                style={{ width: `${task.percent}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 w-10">{task.percent}%</span>
          </div>
        )}
      </div>

      {/* Cancel button - only for running tasks */}
      {isRunning && (
        <button
          onClick={handleCancel}
          disabled={isCancelling}
          className="flex-shrink-0 px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          title="Cancel"
        >
          {isCancelling ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Cancelling...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Cancel</span>
            </>
          )}
        </button>
      )}

      {/* Dismiss button - only for completed/error/cancelled */}
      {!isRunning && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded"
          title="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default TaskProgressBar;
